import type { AnimBeginActionNode } from "../../types.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
  ScheduledNode,
} from "../types.js";
import {
  coerceCssAnimationDirection,
  coerceCssAnimationFillMode,
  emptyPatchResult,
  toFiniteNumber,
} from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class AnimBeginBehaviorNode extends BehaviorNode {
  declare behavior: AnimBeginActionNode;

  constructor(behavior: AnimBeginActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch: PatchResult = emptyPatchResult();
    const visiting = new Set<string>();

    const durationMs = Math.max(
      0,
      toFiniteNumber(this.getInput("durationMs", visiting, context), 1000),
    );
    const delayMs = Math.max(
      0,
      toFiniteNumber(this.getInput("delayMs", visiting, context), 0),
    );
    const iterationCount = Math.max(
      1,
      Math.floor(
        toFiniteNumber(this.getInput("iterationCount", visiting, context), 1),
      ),
    );
    const name = String(this.getInput("name", visiting, context) ?? "").trim();

    context.animationDraft = {
      begin: {
        name,
        durationMs,
        delayMs,
        iterationCount,
        direction: coerceCssAnimationDirection(
          this.getInput("direction", visiting, context),
        ),
        fillMode: coerceCssAnimationFillMode(
          this.getInput("fillMode", visiting, context),
        ),
      },
      keyframes: [],
    };

    const nextFlows = this.getNextFlows(context);
    const runtimeSignals: RuntimeSignal[] = [];
    const scheduledNodes: ScheduledNode[] = [];

    if (nextFlows.length === 0 && context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }

    return {
      patch,
      nextFlows,
      scheduledNodes,
      runtimeSignals,
    };
  }

  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }

  private getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }
}
